import { useEffect, useState, useCallback } from "react";
import api, { isAuthError, formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/utils/roles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, Pencil, Plus, Trash2, ShieldCheck, ChevronRight, Users, Search } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

// Traffic-light colour per cell status. Matches the certs chase-list palette
// so the visual language is consistent across the CRM.
const CELL_STYLES = {
  ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expiring_soon: "bg-amber-100 text-amber-900 border-amber-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  missing: "bg-slate-50 text-slate-400 border-slate-200",
};
const CELL_LABEL = { ok: "OK", expiring_soon: "Soon", expired: "Expired", missing: "—" };

export default function TrainingMatrixPage() {
  useDocumentTitle("Training Matrix", "Per-trainer assessor competencies.");
  const { user } = useAuth();
  const canEdit = isSuperAdmin(user?.role);

  const [matrix, setMatrix] = useState({ trainers: [], competencies: [] });
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null);  // { trainer, competency, cell }
  const [form, setForm] = useState({ cert_number: "", issue_date: "", expiry_date: "", notes: "" });
  const [newCompOpen, setNewCompOpen] = useState(false);
  const [newComp, setNewComp] = useState({ code: "", name: "", category: "" });
  // Which trainer rows are expanded; collapsed by default so the list is
  // just a tidy name directory until Jamie drills in.
  const [openIds, setOpenIds] = useState({});
  const [search, setSearch] = useState("");

  const toggleOpen = (id) => setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/training-matrix/matrix");
      setMatrix(data);
    } catch (err) {
      if (!isAuthError(err)) toast.error(formatApiError(err.response?.data?.detail) || "Failed to load matrix");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (trainer, competency, cell) => {
    if (!canEdit) return;
    setEditCell({ trainer, competency, cell });
    setForm({
      cert_number: cell?.cert_number || "",
      issue_date: cell?.issue_date || "",
      expiry_date: cell?.expiry_date || "",
      notes: cell?.notes || "",
    });
  };

  const saveEntry = async () => {
    try {
      await api.put("/training-matrix/entry", {
        trainer_id: editCell.trainer.trainer_id,
        competency_id: editCell.competency.id,
        ...form,
      });
      toast.success("Saved");
      setEditCell(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const createCompetency = async () => {
    const code = newComp.code.trim().toUpperCase();
    const name = newComp.name.trim();
    if (!code || !name) return;
    try {
      await api.post("/training-matrix/competencies", {
        code, name, category: newComp.category.trim(), order: 999,
      });
      toast.success("Competency added");
      setNewCompOpen(false);
      setNewComp({ code: "", name: "", category: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const deleteCompetency = async (c) => {
    if (!window.confirm(`Delete competency "${c.name}"? This clears the whole column across every trainer.`)) return;
    try {
      await api.delete(`/training-matrix/competencies/${c.id}`);
      toast.success("Competency deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground text-sm" data-testid="matrix-loading">Loading matrix…</div>;

  return (
    <div className="space-y-4" data-testid="training-matrix-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-semibold flex items-center gap-2">
            <GraduationCap size={18} /> Training Matrix
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Assessor competencies per trainer. {canEdit ? "Click any cell to log a certificate." : "View-only — ask a superadmin to update entries."}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setNewCompOpen(true)} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-competency-btn">
            <Plus size={14} className="mr-1" /> New competency
          </Button>
        )}
      </div>

      {matrix.trainers.length === 0 ? (
        <div className="rounded-lg border bg-white p-10 text-center text-sm text-muted-foreground">
          No trainers yet. Add trainers via the Courses page and they'll appear here.
        </div>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search trainer…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="matrix-search" />
          </div>
          <div className="space-y-2" data-testid="matrix-trainer-list">
            {matrix.trainers
              .filter(t => !search || (t.trainer_name || "").toLowerCase().includes(search.toLowerCase()) || (t.trainer_email || "").toLowerCase().includes(search.toLowerCase()))
              .map(t => {
                const buckets = { ok: 0, expiring_soon: 0, expired: 0, missing: 0 };
                const held = (t.cells || []).filter(c => c.status !== "missing");
                held.forEach(c => { buckets[c.status] = (buckets[c.status] || 0) + 1; });
                buckets.missing = matrix.competencies.length - held.length;
                const open = !!openIds[t.trainer_id];
                return (
                  <div key={t.trainer_id} className="border border-border rounded-md bg-white overflow-hidden group" data-testid={`row-${t.trainer_id}`}>
                    <button
                      type="button"
                      onClick={() => toggleOpen(t.trainer_id)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                      data-testid={`toggle-${t.trainer_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <ChevronRight size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                        <Users size={14} className="text-slate-500 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{t.trainer_name}</div>
                          {t.trainer_email && <div className="text-[11px] text-muted-foreground truncate">{t.trainer_email}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
                        {buckets.ok > 0 && <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">{buckets.ok} OK</Badge>}
                        {buckets.expiring_soon > 0 && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">{buckets.expiring_soon} soon</Badge>}
                        {buckets.expired > 0 && <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">{buckets.expired} expired</Badge>}
                        <span className="text-muted-foreground">· {buckets.missing} unassigned</span>
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-border bg-slate-50/50 p-2" data-testid={`body-${t.trainer_id}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                          {matrix.competencies.map(c => {
                            const cell = t.cells.find(x => x.competency_id === c.id) || { status: "missing" };
                            const style = CELL_STYLES[cell.status];
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => openEdit(t, c, cell)}
                                disabled={!canEdit}
                                className={`flex items-center justify-between gap-2 rounded px-2.5 py-1.5 text-[11px] border text-left ${style} ${canEdit ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
                                data-testid={`cell-${t.trainer_id}-${c.code}`}
                                title={cell.expiry_date ? `Expires ${cell.expiry_date}` : (cell.cert_number || c.name)}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">{c.name}</div>
                                  <div className="text-[10px] opacity-75 truncate">{c.category}</div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-semibold">{CELL_LABEL[cell.status]}</div>
                                  {cell.expiry_date && cell.status !== "missing" && (
                                    <div className="text-[10px] opacity-75">{cell.expiry_date}</div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}

      {/* Cell edit dialog */}
      <Dialog open={editCell !== null} onOpenChange={(v) => { if (!v) setEditCell(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-cell-dialog">
          {editCell && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <ShieldCheck size={16} /> {editCell.competency.name}
                </DialogTitle>
                <p className="text-xs text-muted-foreground">{editCell.trainer.trainer_name}</p>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Certificate / awarding body ref</Label>
                  <Input value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} placeholder="e.g. CCN1-2024-123" className="h-9" data-testid="entry-cert-number" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Issue date</Label>
                    <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="h-9" data-testid="entry-issue-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Expiry (blank = no expiry)</Label>
                    <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="h-9" data-testid="entry-expiry-date" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full text-sm border border-border rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-slate-400" placeholder="Any caveats or scope limits" data-testid="entry-notes" />
                </div>
                <p className="text-[11px] text-muted-foreground">Clear all fields to remove this cell entirely.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditCell(null)}>Cancel</Button>
                <Button onClick={saveEntry} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-cell-btn">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New competency dialog */}
      <Dialog open={newCompOpen} onOpenChange={setNewCompOpen}>
        <DialogContent className="max-w-md" data-testid="new-competency-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Plus size={16} /> New competency</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Code</Label>
              <Input value={newComp.code} onChange={(e) => setNewComp({ ...newComp, code: e.target.value.toUpperCase() })} placeholder="e.g. HEATPUMP" className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={newComp.name} onChange={(e) => setNewComp({ ...newComp, name: e.target.value })} placeholder="e.g. Heat Pump Assessor" className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Category (optional)</Label>
              <Input value={newComp.category} onChange={(e) => setNewComp({ ...newComp, category: e.target.value })} placeholder="e.g. Renewables" className="h-9" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCompOpen(false)}>Cancel</Button>
            <Button onClick={createCompetency} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-comp-btn" disabled={!newComp.code || !newComp.name}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
